import { memo, useCallback } from 'react';
import styled from 'styled-components';

import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Box,
} from '@frontend/components';
import type { MultiMap } from '@frontend/datatypes';

import { ENTITY_TYPES, ENVIRONMENT_TYPES, PART_TYPES } from '../constants';
import type { AssemblyInstance } from '../loaders/assemblyInstance';
import type { AnyBody } from '../loaders/body';
import type { StepFaceModel } from '../loaders/step/StepFaceModel';
import type {
  AnyIntersectionTest,
  IntersectionObject,
} from '../renderer/intersection-utils';

//margin should aliged ActionListItem's padding (negative values)
const Actions = styled.ul`
  margin: -8px -12px;
`;

//padding should aliged Actions' margin (positive values)
const ActionListItem = styled.li`
  button {
    width: 100%;
    text-align: left;
    outline: 0;
    padding: 8px 12px;
    transition: all 0.2s ease;

    :hover {
      cursor: pointer;
      background: #ecf6fd;
    }
    :focus,
    :active,
    :focus:active {
      background: #d2eafb;
    }
  }

  :last-of-type {
    margin-bottom: 0;
  }
`;

const Spacer = styled.div`
  border-bottom: 1px solid #ddd;
`;

type PopoverPosition = {
  top: number;
  left: number;
};

type HasPopoverPosition = {
  popoverPosition?: PopoverPosition;
};

const DummyDiv = styled.div<HasPopoverPosition>`
  position: fixed;

  ${(props) =>
    props.popoverPosition && props.popoverPosition.top
      ? `
      top: ${props.popoverPosition.top}px;
      left: ${props.popoverPosition.left}px;
    `
      : ''};
`;

type InteractionPopoverOptions = {
  bodyIdsByPartFileUuid: MultiMap<string, string>;
  canClearSelection?: boolean;
  canCreateSelection?: boolean;
  canZoomToSelection?: boolean;
  closePopover: () => void;
  handleClearSelection: () => void;
  handleCreateSelection: (popoverIntersection: AnyIntersectionTest) => void;
  handleFocus: (bodyId: string) => void;
  popoverIntersection: AnyIntersectionTest;
  handleFocusAllInstances: (bodyId: string) => void;
  handleHide: (bodyId: string) => void;
  handleIsolate: (bodyId: string) => void;
  handleShowAllBodies: (partFileUuid: string) => void;
  handleViewNormalTo: (intersection: AnyIntersectionTest) => void;
  handleZoomToFit: (bodyId: string) => void;
  handleZoomToSelection: () => void;
  isPopoverOpen?: boolean;
  popoverPosition?: PopoverPosition;
  isAssemblyOrSubAssembly: boolean;
  bodyOrAssemblyInstanceById: Map<string, AnyBody | AssemblyInstance>;
  env?: ENVIRONMENT_TYPES;
};

const InteractionPopover = ({
  bodyIdsByPartFileUuid,
  canClearSelection,
  canCreateSelection,
  canZoomToSelection,
  closePopover,
  handleClearSelection,
  handleCreateSelection,
  handleFocus,
  handleFocusAllInstances,
  handleHide,
  handleIsolate,
  handleShowAllBodies,
  handleViewNormalTo,
  handleZoomToFit,
  handleZoomToSelection,
  isPopoverOpen,
  popoverIntersection,
  popoverPosition,
  isAssemblyOrSubAssembly,
  bodyOrAssemblyInstanceById,
  env,
}: InteractionPopoverOptions) => {
  const toggleActiveRep = useCallback(() => {
    const body = bodyOrAssemblyInstanceById.get(popoverIntersection.body);
    if (body) {
      body.toggleActiveRepresentation();
    }
    closePopover();
  }, [popoverIntersection, bodyOrAssemblyInstanceById, closePopover]);

  const canZoomToFit = !!popoverIntersection?.object;
  // TODO: line out the different types in constants
  const canViewNormalTo =
    popoverIntersection &&
    popoverIntersection.object &&
    popoverIntersection.type === ENTITY_TYPES.FACE &&
    popoverIntersection.face &&
    (popoverIntersection.object as StepFaceModel)?.metaData?.type === 'plane';

  //Note: You cannot focus on a part that is an outcome of an interrogation because this is how you select a part
  //for interrogation. Only parts that were not derived from interrogation are focusable
  //Example: Unfolded parts are derived from interrogation and you can't interrogtate an unfolded part.
  const canFocus =
    popoverIntersection &&
    popoverIntersection.part &&
    !popoverIntersection.part.isDerivedFromInterrogation;
  const canIsolate = !!(popoverIntersection && popoverIntersection.part);
  const canHide = !!(popoverIntersection && popoverIntersection.part);

  const canSelect =
    canCreateSelection &&
    canHide &&
    popoverIntersection.type !== ENTITY_TYPES.SimpRep; //can't select a simpRep

  //Count occurances of the selected part to determine if action "Focus All Instances" is available
  const part = popoverIntersection && popoverIntersection.part;
  const bodyIds = (part && bodyIdsByPartFileUuid.get(part.id as string)) || [];
  const instanceCount = bodyIds.length;

  // Get rootBodyOrAssemblyInstanceId for showing all bodies of root
  const rootBodyOrAssemblyInstanceId = (
    popoverIntersection?.object as IntersectionObject<PART_TYPES.STEP>
  )?.rootBodyOrAssemblyInstanceId;

  // Prevent empty Popover from rendering
  // Current case is right click not on part body (used to display 'show all bodies', now does not display)
  if (
    !(
      canZoomToFit ||
      canZoomToSelection ||
      canViewNormalTo ||
      canFocus ||
      canHide ||
      canClearSelection ||
      rootBodyOrAssemblyInstanceId ||
      (isAssemblyOrSubAssembly && rootBodyOrAssemblyInstanceId) ||
      (isAssemblyOrSubAssembly && canIsolate)
    )
  ) {
    return <></>;
  }

  //Notes:
  //* Wrapped in <Box></Box> with zIndex 10 so that InteractionPopover is above the viewer-component
  //  (previously DummyDiv target for Popover had zIndex 10, but this did not work for pcad)
  //* PopoverContent borderRadius is 0, _focusVisible is false and width is 'unset' to match previous
  //  styling with antd popover. (unset is necessary for popover's width to fit content)
  return (
    <Box zIndex={10}>
      <Popover
        isOpen={isPopoverOpen}
        onClose={closePopover}
        size="sm"
        placement="left-start"
      >
        <PopoverTrigger>
          <DummyDiv popoverPosition={popoverPosition} />
        </PopoverTrigger>
        <PopoverContent
          borderRadius={0}
          _focusVisible={undefined}
          width="unset"
        >
          <PopoverBody>
            <Actions>
              {canZoomToFit && (
                <ActionListItem>
                  <button
                    onClick={() => handleZoomToFit(popoverIntersection.body)}
                  >
                    Zoom To Fit
                  </button>
                </ActionListItem>
              )}
              {canZoomToSelection && (
                <ActionListItem>
                  <button onClick={handleZoomToSelection}>
                    Zoom To Selection
                  </button>
                </ActionListItem>
              )}
              {canViewNormalTo && (
                <ActionListItem>
                  <button
                    onClick={() => handleViewNormalTo(popoverIntersection)}
                  >
                    View Normal To
                  </button>
                </ActionListItem>
              )}
              {(canZoomToFit || canZoomToSelection || canViewNormalTo) && (
                <Spacer />
              )}
              {canSelect && (
                <ActionListItem>
                  <button
                    onClick={() => handleCreateSelection(popoverIntersection)}
                  >
                    Create Selection
                  </button>
                </ActionListItem>
              )}
              {canClearSelection && (
                <ActionListItem>
                  <button onClick={() => handleClearSelection()}>
                    Clear Selection
                  </button>
                </ActionListItem>
              )}
              {((canCreateSelection && canHide) || canClearSelection) && (
                <Spacer />
              )}
              {canFocus && (
                <ActionListItem>
                  <button onClick={() => handleFocus(popoverIntersection.body)}>
                    Focus
                  </button>
                </ActionListItem>
              )}
              {canFocus && instanceCount > 1 && (
                <ActionListItem>
                  <button
                    onClick={() =>
                      handleFocusAllInstances(popoverIntersection.body)
                    }
                  >
                    {`Focus All Instances (${instanceCount})`}
                  </button>
                </ActionListItem>
              )}
              {isAssemblyOrSubAssembly && canFocus && <Spacer />}
              {isAssemblyOrSubAssembly && canIsolate && (
                <ActionListItem>
                  <button
                    onClick={() => handleIsolate(popoverIntersection.body)}
                  >
                    Isolate
                  </button>
                </ActionListItem>
              )}
              {isAssemblyOrSubAssembly && canHide && (
                <ActionListItem>
                  <button onClick={() => handleHide(popoverIntersection.body)}>
                    Hide
                  </button>
                </ActionListItem>
              )}
              {isAssemblyOrSubAssembly && rootBodyOrAssemblyInstanceId && (
                <ActionListItem>
                  <button
                    onClick={() =>
                      handleShowAllBodies(rootBodyOrAssemblyInstanceId)
                    }
                  >
                    Show All Bodies
                  </button>
                </ActionListItem>
              )}
              {part && env === ENVIRONMENT_TYPES.DEV && (
                <ActionListItem>
                  <button onClick={toggleActiveRep}>
                    Toggle SimpRep/FullDetailRep
                  </button>
                </ActionListItem>
              )}
            </Actions>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default memo(InteractionPopover);
